import { useSnackbar } from 'notistack'
import { useCallback, useState } from 'react'
import { useDispatch } from 'react-redux'
import {
  createPeriodicalReport as createPeriodicalReportService,
  clonePeriodicalReport as clonePeriodicalReportService,
  updatePeriodicalReport as updatePeriodicalReportService,
  deletePeriodicalReport as deletePeriodicalReportService,
  getPeriodicalReportSkipLimit as getPeriodicalReportSkipLimitService,
  setAsCurrentPeriodicalReport as setAsCurrentPeriodicalReportService,
  addAssessmentAreaToPeriodicalReport as addAssessmentAreaToPeriodicalReportService,
  updateAssessmentAreaToPeriodicalReport as updateAssessmentAreaToPeriodicalReportService,
  removeAssessmentAreaFromPeriodicalReport as removeAssessmentAreaFromPeriodicalReportService,
  getPeriodicalReportById as getPeriodicalReportByIdService,
  generatePeriodicalReportMarking as generatePeriodicalReportMarkingService,
  generatePeriodicalReportMarkingByAssessmentArea as generatePeriodicalReportMarkingByAssessmentAreaService,
  generatePeriodicalReportXML as generatePeriodicalReportXMLService
} from '../../services/periodical-report/periodical-report.service.js'
import { removeDuplicates, validate } from '../../utils/index.js'
import { handlerErrorV2 } from '../../utils/errors.jsx'
import {
  updatePeriodicalReportForm,
  updatePeriodicalReportList,
  updatePeriodicalReportListMethod,
  updatePeriodicalReportYearLevels,
  updatePeriodicalReportLearningAreas,
  updatePeriodicalReportAssessmentAreas
} from '../../store/actions/periodical-report.actions.js'
import isEmpty from 'lodash/isEmpty'
import { toggleAlertInfo, toggleAlertModule } from '../../store/actions/alert.actions.js'

const usePeriodicalReport = () => {
  const { enqueueSnackbar } = useSnackbar()
  const dispatch = useDispatch()
  const [isLoadingList, setIsLoadingList] = useState(false)
  const [isLoading, setIsLoading] = useState(false)

  const getPeriodicalReport = useCallback(async (skip, rowsPerPage, filter) => {
    setIsLoadingList(true)
    try {
      const filterData = {
        name: filter?.name,
        year: filter?.year,
        period: filter?.periodId,
        status: filter?.status
      }
      const response = await getPeriodicalReportSkipLimitService(skip, rowsPerPage, filterData)
      if (!response?.data && response?.error) {
        enqueueSnackbar(response.error.message || 'An error ocurred', {
          variant: 'error'
        })
      } else {
        dispatch(updatePeriodicalReportList(response?.data || { users: [], count: 0 }))
      }
    } catch (error) {
      const message = handlerErrorV2(error)
      enqueueSnackbar(message, { variant: 'error' })
    } finally {
      setIsLoadingList(false)
    }
  }, [])

  const getPeriodicalReportById = async (periodicalReportId) => {
    try {
      const response = await getPeriodicalReportByIdService(periodicalReportId)
      if (!response.data && response.error) {
        return {
          error: true,
          message: response.error.message || 'An error ocurred'
        }
      } else {
        const data = response.data
        dispatch(
          updatePeriodicalReportForm({
            ...data,
            enableRatings: data?.ratings.length > 0,
            enableEffortAndConduct: data?.effortAndConductOptions.length > 0,
            previousPeriodicalReportId: data?.previousPeriodicalReportId?._id,
            previousPeriodicalReport: data?.previousPeriodicalReportId || {}
          })
        )

        let assessmentAreas = []
        let learningAreas = []
        let yearLevels = []
        if (data?.assessmentAreas) {
          data?.assessmentAreas?.forEach((area) => {
            const findLA = learningAreas?.find((item) => item._id === area?.assessmentData?.elementId)
            if (!findLA) {
              learningAreas.push({
                _id: area?.assessmentData?.elementId,
                name: area?.assessmentData?.elementName || area?.assessmentData?.elementId,
                curriculum: area?.curriculumName || area?.curriculumId
              })
            }
            const findAA = assessmentAreas.find((item) => item._id === area?.assessmentData?.subElementId)
            if (!findAA) {
              assessmentAreas.push({
                _id: area?.assessmentData?.subElementId,
                name: area?.assessmentData?.name,
                elementId: area?.assessmentData?.elementId,
                curriculum: area?.curriculumName || area?.curriculumId
              })
            }

            yearLevels = [...yearLevels, ...area.yearLevel]
          })

          yearLevels = removeDuplicates(yearLevels).sort((a, b) => a.position - b.position)
          dispatch(updatePeriodicalReportYearLevels(yearLevels))
          dispatch(updatePeriodicalReportLearningAreas(learningAreas))
          dispatch(updatePeriodicalReportAssessmentAreas(assessmentAreas))
        }

        return { error: false, data: response.data }
      }
    } catch (error) {
      const message = handlerErrorV2(error)
      return { error: true, message }
    }
  }

  const validateForm = (form, type = 'create') => {
    let fieldRules = {}
    if (type === 'xml') {
      fieldRules = {
        file: 'required'
      }
    }

    if (type === 'create') {
      fieldRules = {
        code: 'required|withoutSpaces',
        name: 'required',
        year: 'required',
        period: 'required'
      }
    }
    if (type === 'basic-information') {
      fieldRules = {
        code: 'required|withoutSpaces',
        name: 'required',
        year: 'required',
        period: 'required'
      }
    }
    if (type === 'status') {
      fieldRules = {
        status: 'required'
      }
    }
    if (type === 'assessment-area') {
      fieldRules = {
        codeSelected: 'required',
        yearLevelId: 'required'
      }
    }

    const errors = validate(fieldRules, form)
    if (!isEmpty(errors)) {
      return { error: true, ...errors }
    }

    return { error: false }
  }

  const formData = (form, type = 'create') => {
    if (type === 'basic-information') {
      return {
        code: form.code,
        name: form.name,
        year: form.year,
        period: form.period,
        previousPeriodicalReportId: form.previousPeriodicalReportId,
        enableEffortAndConduct: form.enableEffortAndConduct,
        enableRatings: form.enableRatings,
        previousAssessmentData: form.previousAssessmentData || []
      }
    }

    if (type === 'status') {
      return {
        status: form.status
      }
    }

    if (type === 'assessment-area') {
      return {
        assessmentData: form.assessmentData,
        subject: form.subjectId,
        yearLevel: form.yearLevelId,
        curriculumId: form.curriculumId,
        previousAssessmentData: form.previousAssessmentData || []
      }
    }

    if (type === 'assessment-areas') {
      return {
        assessmentAreas: form.assessmentAreas,
        previousAssessmentData: form.previousAssessmentData || []
      }
    }

    return {
      code: form.code,
      name: form.name,
      year: form.year,
      period: form.period,
      status: form.status,
      assessmentAreas: form.assessmentAreas || [],
      previousPeriodicalReportId: form.previousPeriodicalReportId,
      enableEffortAndConduct: form.enableEffortAndConduct,
      enableRatings: form.enableRatings,
      previousAssessmentData: form.previousAssessmentData || []
    }
  }

  const createPeriodicalReport = async (form = {}) => {
    const result = validateForm(form)
    if (result.error) {
      return result
    }

    try {
      const data = formData(form)
      const response = await createPeriodicalReportService(data)
      if (!response.data && response.error) {
        return {
          error: true,
          message: response.error.message || 'An error ocurred'
        }
      } else {
        dispatch(updatePeriodicalReportListMethod('POST', response.data))
        enqueueSnackbar('Periodical Report created successfully', {
          variant: 'success'
        })
        dispatch(toggleAlertModule())

        return { error: false }
      }
    } catch (error) {
      const message = handlerErrorV2(error)
      return { error: true, message }
    }
  }

  const clonePeriodicalReport = async (periodicalReportId) => {
    try {
      const response = await clonePeriodicalReportService(periodicalReportId)
      if (!response.data && response.error) {
        return {
          error: true,
          message: response.error.message || 'An error ocurred'
        }
      } else {
        dispatch(updatePeriodicalReportListMethod('POST', response.data))
        enqueueSnackbar('Periodical Report  successfully', {
          variant: 'success'
        })
        return { error: false }
      }
    } catch (error) {
      const message = handlerErrorV2(error)
      return { error: true, message }
    }
  }

  const updatePeriodicalReport = async (periodicalReportId, type, form = {}) => {
    if (!['basic-information', 'status'].includes(type)) {
      return { error: true, message: 'Update not allowed' }
    }

    const result = validateForm(form, type)
    if (result.error) {
      return result
    }

    try {
      const data = formData(form, type)
      if (type === 'status' && data.status === 'open-for-marking') {
        dispatch(
          toggleAlertInfo(
            true,
            'Updating status for marking',
            'Please wait as this may take a few minutes',
            'loading'
          )
        )
      }
      const response = await updatePeriodicalReportService(periodicalReportId, type, data)
      if (!response.data && response.error) {
        return {
          error: true,
          message: response.error.message || 'An error ocurred'
        }
      } else {
        getPeriodicalReportById(periodicalReportId)
        dispatch(updatePeriodicalReportListMethod('PUT', response.data))
        enqueueSnackbar('Periodical Report updated successfully', {
          variant: 'success'
        })
        dispatch(toggleAlertModule())
        dispatch(toggleAlertModule())

        return { error: false }
      }
    } catch (error) {
      const message = handlerErrorV2(error)
      return { error: true, message }
    } finally {
      dispatch(toggleAlertInfo())
    }
  }

  const setAsCurrentPeriodicalReport = async (periodicalReportId) => {
    setIsLoading(true)
    try {
      const response = await setAsCurrentPeriodicalReportService(periodicalReportId)
      if (!response.data && response.error) {
        return {
          error: true,
          message: response.error.message || 'An error ocurred'
        }
      }
      dispatch(updatePeriodicalReportListMethod('PUT', response.data))
      enqueueSnackbar('Periodical Report set as current successfully', {
        variant: 'success'
      })
      getPeriodicalReportById(periodicalReportId)
      return { error: false }
    } catch (error) {
      const message = handlerErrorV2(error)
      return { error: true, message }
    } finally {
      setIsLoading(false)
    }
  }

  const addAssessmentAreaToPeriodicalReport = async (periodicalReportId, form = {}) => {
    const result = validateForm(form, 'assessment-area')
    if (result.error) {
      return result
    }

    try {
      const data = formData(form, 'assessment-area')
      const response = await addAssessmentAreaToPeriodicalReportService(periodicalReportId, data)
      if (!response.data && response.error) {
        return {
          error: true,
          message: response.error.message || 'An error ocurred'
        }
      } else {
        getPeriodicalReportById(periodicalReportId)
        enqueueSnackbar('Periodical Report updated successfully', {
          variant: 'success'
        })
        dispatch(toggleAlertModule())

        return { error: false }
      }
    } catch (error) {
      const message = handlerErrorV2(error)
      return { error: true, message }
    }
  }

  const updateAssessmentAreaToPeriodicalReport = async (periodicalReportId, assessmentAreaId, form = {}) => {
    const result = validateForm(form, 'assessment-area')
    if (result.error) {
      return result
    }

    try {
      const data = formData(form, 'assessment-area')
      const response = await updateAssessmentAreaToPeriodicalReportService(
        periodicalReportId,
        assessmentAreaId,
        data
      )
      if (!response.data && response.error) {
        return {
          error: true,
          message: response.error.message || 'An error ocurred'
        }
      } else {
        getPeriodicalReportById(periodicalReportId)
        enqueueSnackbar('Periodical Report updated successfully', {
          variant: 'success'
        })
        dispatch(toggleAlertModule())

        return { error: false }
      }
    } catch (error) {
      const message = handlerErrorV2(error)
      return { error: true, message }
    }
  }

  const removeAssessmentAreaFromPeriodicalReport = async (periodicalReportId, assessmentAreaId) => {
    try {
      const response = await removeAssessmentAreaFromPeriodicalReportService(
        periodicalReportId,
        assessmentAreaId
      )
      if (!response.data && response.error) {
        return {
          error: true,
          message: response.error.message || 'An error ocurred'
        }
      } else {
        getPeriodicalReportById(periodicalReportId)
        enqueueSnackbar('Periodical Report updated successfully', {
          variant: 'success'
        })
        dispatch(toggleAlertModule())

        return { error: false }
      }
    } catch (error) {
      const message = handlerErrorV2(error)
      return { error: true, message }
    }
  }

  const deletePeriodicalReport = async (periodicalReportId) => {
    setIsLoading(true)
    try {
      const response = await deletePeriodicalReportService(periodicalReportId)
      if (!response.data && response.error) {
        return {
          error: true,
          message: response.error.message || 'An error ocurred'
        }
      } else {
        dispatch(updatePeriodicalReportListMethod('DELETE', periodicalReportId))
        enqueueSnackbar('Periodical Report deleted successfully', {
          variant: 'success'
        })

        return { error: false }
      }
    } catch (error) {
      const message = handlerErrorV2(error)
      return { error: true, message }
    } finally {
      setIsLoading(false)
    }
  }

  const generatePeriodicalReportXML = async (periodicalReportId, form = {}) => {
    const result = validateForm(form, 'xml')
    if (result.error) {
      return result
    }

    try {
      dispatch(
        toggleAlertInfo(true, 'Uploading file', 'Please wait as this may take a few minutes', 'loading')
      )
      const formData = new FormData()
      formData.append('file', form.file[0])
      const response = await generatePeriodicalReportXMLService(periodicalReportId, formData)
      if (!response.data && response.error) {
        return {
          error: true,
          message: response.error.message || 'An error ocurred'
        }
      } else {
        const filename = response?.data
        const path = `periodical-report/${filename}`
        getPeriodicalReportById(periodicalReportId)
        return { error: false, filename, path }
      }
    } catch (error) {
      const message = handlerErrorV2(error)
      return { error: true, message }
    } finally {
      dispatch(toggleAlertInfo())
    }
  }

  const generatePeriodicalReportMarking = async (periodicalReportId) => {
    setIsLoading(true)
    try {
      dispatch(
        toggleAlertInfo(
          true,
          'Generating Periodical Report data',
          'Please wait as this may take a few minutes',
          'loading'
        )
      )
      const response = await generatePeriodicalReportMarkingService(periodicalReportId)
      if (!response.data && response.error) {
        return {
          error: true,
          message: response.error.message || 'An error ocurred'
        }
      } else {
        enqueueSnackbar('Periodical Report data generated successfully', {
          variant: 'success'
        })
        getPeriodicalReportById(periodicalReportId)

        return { error: false }
      }
    } catch (error) {
      const message = handlerErrorV2(error)
      return { error: true, message }
    } finally {
      setIsLoading(false)
      dispatch(toggleAlertInfo(false))
    }
  }

  const generatePeriodicalReportMarkingByAssessmentArea = async (periodicalReportId, assessmentAreaId) => {
    setIsLoading(true)
    try {
      dispatch(
        toggleAlertInfo(
          true,
          'Generating Periodical Report data',
          'Please wait as this may take a few minutes',
          'loading'
        )
      )
      const response = await generatePeriodicalReportMarkingByAssessmentAreaService(
        periodicalReportId,
        assessmentAreaId
      )
      if (!response.data && response.error) {
        return {
          error: true,
          message: response.error.message || 'An error ocurred'
        }
      } else {
        enqueueSnackbar('Periodical Report data generated successfully', {
          variant: 'success'
        })
        getPeriodicalReportById(periodicalReportId)

        return { error: false }
      }
    } catch (error) {
      const message = handlerErrorV2(error)
      return { error: true, message }
    } finally {
      setIsLoading(false)
      dispatch(toggleAlertInfo(false))
    }
  }

  return {
    isLoadingList,
    isLoading,
    validateFormPeriodicalReport: validateForm,
    getPeriodicalReport,
    createPeriodicalReport,
    clonePeriodicalReport,
    updatePeriodicalReport,
    setAsCurrentPeriodicalReport,
    deletePeriodicalReport,

    addAssessmentAreaToPeriodicalReport,
    updateAssessmentAreaToPeriodicalReport,
    removeAssessmentAreaFromPeriodicalReport,

    getPeriodicalReportById,
    generatePeriodicalReportXML,
    generatePeriodicalReportMarking,
    generatePeriodicalReportMarkingByAssessmentArea
  }
}

export default usePeriodicalReport
